import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Briefcase, Calendar, Building, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const JobListingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedDepartments = location.state?.departments || [];

  const [filters, setFilters] = useState({
    state: '',
    city: '',
    departments: preselectedDepartments
  });

  // Mock job data - in production, this would come from backend
  const mockJobs = [
    {
      id: 1,
      title: 'Art Director Needed for Period Drama',
      departments: ['art-department'],
      state: 'Maharashtra',
      city: 'Mumbai',
      description: 'Looking for experienced art director for a period drama set in 1940s India. Must have portfolio showcasing period work.',
      postedBy: 'Rainbow Productions',
      budget: '₹25,000 - ₹35,000/day',
      duration: '45 days',
      startDate: '2025-02-15',
      postedDate: '2 days ago'
    },
    {
      id: 2,
      title: 'Cinematographer - Feature Film',
      departments: ['camera-department'],
      state: 'Karnataka',
      city: 'Bangalore',
      description: 'Seeking skilled cinematographer for indie feature film. Experience with RED cameras preferred.',
      postedBy: 'Indie Vision Studios',
      budget: '₹30,000/day',
      duration: '30 days',
      startDate: '2025-03-01',
      postedDate: '5 days ago'
    },
    {
      id: 3,
      title: 'Sound Engineer - Web Series',
      departments: ['sound-department'],
      state: 'Maharashtra',
      city: 'Mumbai',
      description: 'Need experienced sound engineer for 8-episode web series. Location recording and mixing required.',
      postedBy: 'StreamFlix Productions',
      budget: 'Negotiable',
      duration: '60 days',
      startDate: '2025-02-20',
      postedDate: '1 week ago'
    },
    {
      id: 4,
      title: 'VFX Artist - Sci-Fi Feature',
      departments: ['sfx-vfx'],
      state: 'Tamil Nadu',
      city: 'Chennai',
      description: 'Looking for VFX artist with experience in sci-fi genre. Maya, Houdini knowledge essential.',
      postedBy: 'Cosmos Films',
      budget: '₹40,000/day',
      duration: '90 days',
      startDate: '2025-02-10',
      postedDate: '3 days ago'
    },
    {
      id: 5,
      title: 'Production Manager - Commercial Shoot',
      departments: ['production'],
      state: 'Delhi',
      city: 'New Delhi',
      description: 'Experienced production manager needed for high-budget commercial campaign.',
      postedBy: 'AdVantage Media',
      budget: '₹20,000/day',
      duration: '15 days',
      startDate: '2025-02-05',
      postedDate: '4 days ago'
    },
    {
      id: 6,
      title: 'Editor - Documentary Series',
      departments: ['editing', 'post-production'],
      state: 'Maharashtra',
      city: 'Pune',
      description: 'Need creative editor for 6-part documentary series. Premiere Pro and DaVinci Resolve experience required.',
      postedBy: 'Documentary House',
      budget: '₹18,000/day',
      duration: '40 days',
      startDate: '2025-03-10',
      postedDate: '1 day ago'
    },
    {
      id: 7,
      title: '1st AD - Feature Film',
      departments: ['assistant-directors'],
      state: 'West Bengal',
      city: 'Kolkata',
      description: 'Seeking experienced 1st Assistant Director for Bengali feature film. Must have 5+ feature film credits.',
      postedBy: 'Bengal Talkies',
      budget: '₹22,000/day',
      duration: '55 days',
      startDate: '2025-02-25',
      postedDate: '6 days ago'
    },
    {
      id: 8,
      title: 'Lighting Gaffer - Music Video',
      departments: ['lighting-department'],
      state: 'Maharashtra',
      city: 'Mumbai',
      description: 'Need creative gaffer for high-energy music video shoot. Studio and outdoor experience required.',
      postedBy: 'Rhythm Productions',
      budget: '₹15,000/day',
      duration: '5 days',
      startDate: '2025-02-08',
      postedDate: '2 days ago'
    }
  ];

  const states = ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'West Bengal', 'Telangana', 'Rajasthan', 'Kerala'];
  const cities = {
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur'],
    'Delhi': ['New Delhi', 'Noida', 'Gurgaon'],
    'Karnataka': ['Bangalore', 'Mysore'],
    'Tamil Nadu': ['Chennai', 'Coimbatore'],
    'West Bengal': ['Kolkata', 'Howrah'],
    'Telangana': ['Hyderabad'],
    'Rajasthan': ['Jaipur', 'Udaipur'],
    'Kerala': ['Kochi', 'Thiruvananthapuram']
  };

  const departmentOptions = [
    { id: 'art-department', name: 'Art Department' },
    { id: 'camera-department', name: 'Camera Department' },
    { id: 'sound-department', name: 'Sound Department' },
    { id: 'direction', name: 'Direction' },
    { id: 'makeup-hair', name: 'Makeup & Hair' },
    { id: 'sfx-vfx', name: 'SFX / VFX' },
    { id: 'production', name: 'Production' },
    { id: 'assistant-directors', name: 'Assistant Directors' },
    { id: 'lighting-department', name: 'Lighting Department' },
    { id: 'post-production', name: 'Post Production' },
    { id: 'script-department', name: 'Script Department' },
    { id: 'editing', name: 'Editing' }
  ];

  // Filter jobs based on selected filters
  const filteredJobs = mockJobs.filter(job => {
    const matchesDepartment = filters.departments.length === 0 || 
      filters.departments.some(dept => job.departments.includes(dept));
    const matchesState = !filters.state || job.state === filters.state;
    const matchesCity = !filters.city || job.city === filters.city;
    
    return matchesDepartment && matchesState && matchesCity;
  });

  const toggleDepartment = (deptId) => {
    if (filters.departments.includes(deptId)) {
      setFilters({
        ...filters,
        departments: filters.departments.filter(d => d !== deptId)
      });
    } else {
      setFilters({
        ...filters,
        departments: [...filters.departments, deptId]
      });
    }
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      city: '',
      departments: []
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/discover')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters
                  </h2>
                  {(filters.state || filters.city || filters.departments.length > 0) && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear All
                    </Button>
                  )}
                </div>

                <div className="space-y-6">
                  {/* State Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">State</label>
                    <Select value={filters.state} onValueChange={(val) => setFilters({ ...filters, state: val, city: '' })}>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="All States" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All States</SelectItem>
                        {states.map(state => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* City Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">City</label>
                    <Select 
                      value={filters.city} 
                      onValueChange={(val) => setFilters({ ...filters, city: val })}
                      disabled={!filters.state}
                    >
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="All Cities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Cities</SelectItem>
                        {filters.state && cities[filters.state]?.map(city => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Department Filter */}
                  <div>
                    <label className="text-sm font-medium mb-3 block">Departments</label>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {departmentOptions.map(dept => (
                        <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.departments.includes(dept.id)}
                            onChange={() => toggleDepartment(dept.id)}
                            className="rounded border-border"
                          />
                          <span className="text-sm">{dept.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Job Listings */}
          <div className="lg:col-span-3">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground mb-2">Available Projects</h1>
              <p className="text-muted-foreground">
                Showing {filteredJobs.length} {filteredJobs.length === 1 ? 'job' : 'jobs'}
                {filters.departments.length > 0 && ` in ${filters.departments.length} selected department(s)`}
              </p>
            </div>

            {/* Active Filters Display */}
            {(filters.state || filters.city || filters.departments.length > 0) && (
              <div className="mb-6 flex flex-wrap gap-2">
                {filters.state && (
                  <Badge className="bg-accent/10 text-accent border-accent/30 pr-1">
                    {filters.state}
                    <button
                      onClick={() => setFilters({ ...filters, state: '', city: '' })}
                      className="ml-2 hover:bg-accent/20 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {filters.city && (
                  <Badge className="bg-accent/10 text-accent border-accent/30 pr-1">
                    {filters.city}
                    <button
                      onClick={() => setFilters({ ...filters, city: '' })}
                      className="ml-2 hover:bg-accent/20 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {filters.departments.map(deptId => {
                  const dept = departmentOptions.find(d => d.id === deptId);
                  return (
                    <Badge key={deptId} className="bg-accent/10 text-accent border-accent/30 pr-1">
                      {dept?.name}
                      <button
                        onClick={() => toggleDepartment(deptId)}
                        className="ml-2 hover:bg-accent/20 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Job Cards */}
            <div className="space-y-4">
              {filteredJobs.length > 0 ? (
                filteredJobs.map((job, index) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="bg-card border-border hover:border-accent/50 transition-all hover:shadow-lg">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold text-foreground mb-2 hover:text-accent cursor-pointer">
                              {job.title}
                            </h3>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {job.departments.map(deptId => {
                                const dept = departmentOptions.find(d => d.id === deptId);
                                return (
                                  <Badge key={deptId} className="bg-accent/10 text-accent border-accent/30">
                                    {dept?.name}
                                  </Badge>
                                );
                              })}
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground">{job.postedDate}</span>
                        </div>

                        <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                          {job.description}
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-accent" />
                            <span className="text-muted-foreground">{job.postedBy}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-accent" />
                            <span className="text-muted-foreground">{job.city}, {job.state}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-accent" />
                            <span className="text-muted-foreground">{job.duration}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-accent" />
                            <span className="text-muted-foreground font-medium">{job.budget}</span>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <Button className="bg-accent hover:bg-accent/90 text-white">
                            View Details
                          </Button>
                          <Button variant="outline" className="border-accent text-accent hover:bg-accent hover:text-white">
                            Apply Now
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <Card className="bg-card border-border">
                  <CardContent className="p-12 text-center">
                    <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No Jobs Found</h3>
                    <p className="text-muted-foreground mb-6">
                      No jobs match your current filters. Try adjusting your search criteria.
                    </p>
                    <Button onClick={clearFilters} variant="outline">
                      Clear All Filters
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobListingsPage;
